import { onBeforeUnmount, onMounted, watch, type Ref } from "vue";

type Options = {
  edgePx?: number; // ancho del “borde” sensible (default 24)
  minDx?: number; // mínimo movimiento horizontal (default 12)
  maxDy?: number; // tolerancia vertical (default 30)
  onlyIOS?: boolean; // default true
};

function isIOS(): boolean {
  return /iP(hone|od|ad)/i.test(navigator.userAgent);
}

/**
 * Bloquea el gesto de swipe-back del navegador en iOS (PWA/Safari)
 * cuando hay overlays abiertos (alert/modal/loading/etc).
 *
 * No requiere IonRouterOutlet.
 * No requiere divs “tapando” el borde.
 * Funciona aunque el overlay esté por encima, porque usa listeners en captura.
 */
export function useIOSSwipeBackGuard(
  hasOverlayOpen: Ref<boolean>,
  opts: Options = {}
) {
  const edgePx = opts.edgePx ?? 24;
  const minDx = opts.minDx ?? 12;
  const maxDy = opts.maxDy ?? 30;
  const onlyIOS = opts.onlyIOS ?? true;

  let tracking = false;
  let startX = 0;
  let startY = 0;

  const shouldRun = () => (onlyIOS ? isIOS() : true);

  const onTouchStartCapture = (e: TouchEvent) => {
    if (!shouldRun()) return;
    if (!hasOverlayOpen.value) return;

    const t = e.touches?.[0];
    if (!t) return;

    // Solo empezamos a vigilar si el gesto nace en el borde izquierdo
    if (t.clientX <= edgePx) {
      tracking = true;
      startX = t.clientX;
      startY = t.clientY;

      // Importantísimo: sin esto, Safari ignora preventDefault en move
      // (y además evita que el navegador “enganche” el gesto pronto)
      e.preventDefault();
    }
  };

  const onTouchMoveCapture = (e: TouchEvent) => {
    if (!tracking) return;
    if (!shouldRun()) return;

    const t = e.touches?.[0];
    if (!t) return;

    const dx = t.clientX - startX;
    const dy = Math.abs(t.clientY - startY);

    // Si es un swipe hacia la derecha “suficiente” y no es scroll vertical → bloquear
    if (dx > minDx && dy < maxDy) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const onTouchEndCapture = () => {
    tracking = false;
  };

  const attach = () => {
    // capture:true + passive:false es CLAVE
    document.addEventListener("touchstart", onTouchStartCapture, {
      capture: true,
      passive: false,
    });
    document.addEventListener("touchmove", onTouchMoveCapture, {
      capture: true,
      passive: false,
    });
    document.addEventListener("touchend", onTouchEndCapture, {
      capture: true,
      passive: true,
    });
    document.addEventListener("touchcancel", onTouchEndCapture, {
      capture: true,
      passive: true,
    });
  };

  const detach = () => {
    document.removeEventListener("touchstart", onTouchStartCapture, true);
    document.removeEventListener("touchmove", onTouchMoveCapture, true);
    document.removeEventListener("touchend", onTouchEndCapture, true);
    document.removeEventListener("touchcancel", onTouchEndCapture, true);
  };

  onMounted(() => attach());
  onBeforeUnmount(() => detach());

  // Si quieres, al cerrarse el overlay cortamos tracking por si quedó a medias
  const unwatch = watch(hasOverlayOpen, (open) => {
    if (!open) tracking = false;
  });
  onBeforeUnmount(() => unwatch());
}
